import {
  useQuery,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey,
} from '@tanstack/react-query';
import service from '../service';
import type { ToastGuidGroup } from '../types/toastGuidGroup';

type ToastGuidGroupsResponse = {
  data: ToastGuidGroup[];
  meta?: {
    total: number;
  };
};

type DefaultError = unknown;

export const toastGuidGroupQueryKeys = {
  all: ['toastGuidGroups'] as const,
  lists: () => [...toastGuidGroupQueryKeys.all, 'list'] as const,
  list: (params: Record<string, string>) =>
    [...toastGuidGroupQueryKeys.lists(), params] as const,
  details: () => [...toastGuidGroupQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...toastGuidGroupQueryKeys.details(), id] as const,
};

type UseToastGuidGroupsParams = {
  searchParams?: Record<string, string>;
  enabled?: boolean;
};

export function useToastGuidGroups<TData = ToastGuidGroupsResponse, TError = DefaultError>(
  { searchParams = {}, enabled = true }: UseToastGuidGroupsParams = {},
  options?: Omit<
    UseQueryOptions<ToastGuidGroupsResponse, TError, TData, QueryKey>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
): UseQueryResult<TData, TError> {
  const queryKey: QueryKey = toastGuidGroupQueryKeys.list(searchParams);

  return useQuery<ToastGuidGroupsResponse, TError, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getToastGuidGroups(searchParams),
    ...(options as any),
  });
}
